import Input from "shared/ui/Input";
import "./style.scss";

import React, { FC, useRef, useState } from "react";
import { Field, Form, Formik, useFormikContext } from "formik";

import { ReactComponent as IconEdit } from "assets/img/icons/icon_Edit.svg";
import Button from "shared/ui/Button";
import { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { DocumentBasylic } from "shared/types/types";
import Modal from "shared/ui/Modal";
import Skeleton from "shared/ui/Skeleton";
import Loader from "shared/ui/Loader";
import * as Yup from "yup";
import DocumentTypeSelect from "components/DocumentTypeSelect";
import SelectCountry from "shared/ui/SelectCountry";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Composant interne pour accéder au contexte Formik
const DocumentTypeSelectWithFormik = ({
                                          handleChange,
                                          isVerification,
                                          type
                                      }: {
    handleChange: (value: string) => void;
    isVerification?: boolean;
    type: string;
}) => {
    const { setFieldValue } = useFormikContext();

    const handleTypeChange = (value: string) => {
        // Mettre à jour Formik
        setFieldValue("typeDocument", value);
        // Appeler le handleChange original
        handleChange(value);
    };

    return (
      <DocumentTypeSelect
        handleChange={handleTypeChange}
        isVerification={isVerification}
        type={type}
      />
    );
};

type CheckDocumentInfosProps = {
    isScanning: boolean;
    document?: DocumentBasylic;
    setIsConfirmed?: (isConfirmed: boolean) => void;
    setDocument?: (document: DocumentBasylic) => void;
    isManually?: boolean;
    onChangeType: (type: string) => void;
    isVerification?: boolean,
    type: string;
};

const CheckInfoIDTracker: FC<CheckDocumentInfosProps> = ({
                                                             isScanning,
                                                             document,
                                                             setIsConfirmed,
                                                             setDocument ,
                                                             isManually = false,
                                                             isVerification = false,
                                                             onChangeType,
                                                             type
                                                         }) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<any>([]);
    const [requiredFiles, setRequiredFiles] = useState<number>(1);
    const [isFrench] = useState<boolean>(true);

    // Références pour les inputs
    const numeroRef = useRef<HTMLInputElement>(null);
    const nomRef = useRef<HTMLInputElement>(null);
    const prenomRef = useRef<HTMLInputElement>(null);
    const dateNaissanceRef = useRef<HTMLInputElement>(null);
    const lieuNaissanceRef = useRef<HTMLInputElement>(null);
    const dateExpirationRef = useRef<HTMLInputElement>(null);
    const dateDelivranceRef = useRef<HTMLInputElement>(null);

    // Donne le focus à l'input correspondant
    const focusField = (ref: React.RefObject<HTMLInputElement>) => {
        setTimeout(() => {
            if (ref.current) {
                ref.current.focus();
            }
        }, 0);
    };

    const handleChange = (value: string) => {
        onChangeType(value);
        setSelectedFiles([]);

        if (value === "Passeport") {
            setRequiredFiles(1);
        } else {
            setRequiredFiles(1);
        }
    };

    return (
      <div className="CheckInfoIDTracker card h-100">
          {isScanning ? (
            <>
                {isMobile && <Loader text="Analyse en cours. L'analyse peut prendre jusqu'à 30secondes." />}
                <Skeleton
                  width="100%"
                  height={40}
                />
                <Skeleton
                  width="100%"
                  height={40}
                />
                <Skeleton
                  width="100%"
                  height={40}
                />
                <Skeleton
                  width="100%"
                  height={40}
                />
                <Skeleton
                  width="100%"
                  height={40}
                />
                <Skeleton
                  width="100%"
                  height={40}
                />
            </>
          ) : (
            <>
                <Formik
                  initialValues={{
                      numeroDocument: document?.numeroDocument || "",
                      nom: document?.nom || "",
                      prenom: document?.prenom || "",
                      dateNaissance: document?.dateNaissance || "",
                      lieuNaissance: document?.lieuNaissance || "",
                      dateExpiration: document?.dateExpiration || "",
                      dateDelivrance: document?.dateDelivrance || "",
                      typeDocument: document?.typeDocument?.toLowerCase() || "",
                      country_code: document?.country_code || "FRA",
                  }}
                  onSubmit={(values) => {
                      if (setDocument) {
                          setDocument({
                              ...document,
                              ...values,
                          } as DocumentBasylic);
                      }
                      setIsConfirmed?.(true);
                  }}
                  validationSchema={Yup.object({
                      numeroDocument: Yup.string().required("Le numéro de document est requis"),
                      nom: Yup.string()
                        .min(2, "Le nom doit contenir au moins 2 caractères.")
                        .max(20, "Le nom ne peut pas dépasser 20 caractères.")
                        .matches(
                          /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/,
                          "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
                        )
                        .required("Le nom est requis"),
                      prenom: Yup.string()
                        .min(2, "Le prénom doit contenir au moins 2 caractères.")
                        .max(50, "Le prénom ne peut pas dépasser 50 caractères.")
                        .matches(
                          /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/,
                          "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
                        )
                        .required("Le prénom est requis"),
                      dateNaissance: Yup.string()
                        .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, "La date doit être au format JJ/MM/AAAA")
                        .length(10, "La date de naissance doit contenir 10 caractères")
                        .required("La date de naissance est requise"),
                      lieuNaissance: Yup.string().required("Le lieu de naissance est requis"),
                      dateExpiration: Yup.string()
                        .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, "La date doit être au format JJ/MM/AAAA")
                        .length(10, "La date d'expiration doit contenir 10 caractères")
                        .required("La date d'expiration est requise"),
                      dateDelivrance: Yup.string()
                        .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, "La date doit être au format JJ/MM/AAAA")
                        .length(10, "La date de délivrance doit contenir 10 caractères")
                        .required("La date de délivrance est requise"),
                      typeDocument: Yup.string().required(
                        "le type du document est requis",
                      ),
                      country_code: Yup.string().required(
                        "L'indicatif du pays est requis",
                      ),
                  })}>
                    {(formik) => (
                      <Form className="d-flex flex-column justify-content-center align-items-center">
                          <div>
                              <h3>Vérification des informations</h3>
                              <p className="mb-1">Veuillez vérifier la conformité de vos informations présentes
                                  sur le document.</p>

                              <DocumentTypeSelectWithFormik
                                handleChange={handleChange}
                                isVerification={isVerification}
                                type={type}
                              />

                              <div style={{ width: "40%" }} className={"mb-6"}>
                                  {/* Select pour les indicatifs des pays */}
                                  <Field
                                    name="documentCountry"
                                    value={formik.values.country_code}
                                    id="documentCountry"
                                  >
                                      {({ field, country }: any) => (
                                        <SelectCountry
                                          field={field}
                                          country={country}
                                          phone={false}
                                        />
                                      )}
                                  </Field>
                                  {formik.touched.country_code &&
                                    formik.errors.country_code && (
                                      <div className="text-danger small mt-1">
                                          {formik.errors.country_code}
                                      </div>
                                    )}
                              </div>

                              {/* Numéro de document */}
                              <Input
                                required
                                placeholder="Numéro de document"
                                name="numeroDocument"
                                value={formik.values.numeroDocument}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={
                                    formik.touched.numeroDocument && formik.errors.numeroDocument
                                      ? formik.errors.numeroDocument
                                      : ""
                                }
                                inputRef={numeroRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier le numéro de document"
                                      onClick={() => focusField(numeroRef)}
                                    />
                                }
                              />

                              {/* Nom */}
                              <Input
                                required
                                placeholder="Nom"
                                name="nom"
                                value={formik.values.nom}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched.nom && formik.errors.nom ? formik.errors.nom : ""}
                                inputRef={nomRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier le nom"
                                      onClick={() => focusField(nomRef)}
                                    />
                                }
                              />

                              {/* Prénom */}
                              <Input
                                required
                                placeholder="Prénom"
                                name="prenom"
                                value={formik.values.prenom}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched.prenom && formik.errors.prenom ? formik.errors.prenom : ""}
                                inputRef={prenomRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier le prénom"
                                      onClick={() => focusField(prenomRef)}
                                    />
                                }
                              />

                              {/* Date de naissance */}
                              <Input
                                required
                                placeholder="Date de naissance (JJ/MM/AAAA)"
                                name="dateNaissance"
                                maxLength={10}
                                value={formik.values.dateNaissance}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={
                                    formik.touched.dateNaissance && formik.errors.dateNaissance ? formik.errors.dateNaissance : ""
                                }
                                inputRef={dateNaissanceRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier la date de naissance"
                                      onClick={() => focusField(dateNaissanceRef)}
                                    />
                                }
                              />

                              {/* Lieu de naissance */}
                              <Input
                                required
                                placeholder="Lieu de naissance"
                                name="lieuNaissance"
                                value={formik.values.lieuNaissance}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={
                                    formik.touched.lieuNaissance && formik.errors.lieuNaissance ? formik.errors.lieuNaissance : ""
                                }
                                inputRef={lieuNaissanceRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier le lieu de naissance"
                                      onClick={() => focusField(lieuNaissanceRef)}
                                    />
                                }
                              />

                              {/* Date de délivrance */}
                              <Input
                                required
                                placeholder="Date de délivrance (JJ/MM/AAAA)"
                                name="dateDelivrance"
                                maxLength={10}
                                value={formik.values.dateDelivrance}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={
                                    formik.touched.dateDelivrance && formik.errors.dateDelivrance ? formik.errors.dateDelivrance : ""
                                }
                                inputRef={dateDelivranceRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier la date de délivrance"
                                      onClick={() => focusField(dateDelivranceRef)}
                                    />
                                }
                              />

                              {/* Date d'expiration */}
                              <Input
                                required
                                placeholder="Date d'expiration (JJ/MM/AAAA)"
                                name="dateExpiration"
                                maxLength={10}
                                value={formik.values.dateExpiration}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                errorMessage={
                                    formik.touched.dateExpiration && formik.errors.dateExpiration ? formik.errors.dateExpiration : ""
                                }
                                inputRef={dateExpirationRef}
                                rightIcon={
                                    <Button
                                      isIconButton
                                      buttonType="button"
                                      icon={<IconEdit />}
                                      className="icon_button_edit"
                                      disabled={isScanning}
                                      type={Button_Type.PRIMARY}
                                      style={Button_Style.OUTLINED}
                                      title="Modifier la date d'expiration"
                                      onClick={() => focusField(dateExpirationRef)}
                                    />
                                }
                              />
                          </div>

                          <div style={{ marginTop: "auto", marginLeft: "auto" }}>
                              <Button
                                buttonType="button"
                                label="Confirmer"
                                disabled={isScanning || (!formik.isValid && formik.dirty)}
                                className="mt-1"
                                onClick={() => setShowModal(true)}
                              />
                          </div>

                          {showModal && (
                            <Modal
                              onClose={() => setShowModal(false)}
                              isOpen={showModal}
                              title="Avertissement"
                              infoModal
                              type="info">
                                <div
                                  className="w-100 d-flex flex-column justify-content-center align-items-center">
                                    <p className="text-justify">
                                        Veuillez vérifier attentivement l’exactitude de toutes les informations
                                        figurant dans ce
                                        document. Une fois validées, vos données seront chiffrées,
                                        <span className="remark">&nbsp; ce qui empêchera toute modification ultérieure</span>.
                                        <br />
                                        <b>La copie de votre pièce d’identité sera automatiquement supprimée après contrôle</b>,
                                        conformément à notre engagement de confidentialité.
                                        <b>&nbsp;Cette étape garantit la protection totale de vos données personnelles.</b>
                                    </p>
                                    <Button
                                      label="Confirmer"
                                      className="mt-1"
                                      buttonType="submit"
                                    />
                                </div>
                            </Modal>
                          )}
                      </Form>
                    )}
                </Formik>
            </>
          )}
      </div>
    );
};

export default CheckInfoIDTracker;